// src/components/NotificationBell.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getNotifications, getUnreadNotificationCount, markNotificationsAsRead, deleteNotification } from "../api/notifications";
import { onNotificationRefresh } from "../utils/notificationRefresh";
import "./NotificationBell.css";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const loadUnreadCount = React.useCallback(async () => {
    try {
      const data = await getUnreadNotificationCount();
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error("Error loading unread count:", error);
    }
  }, []);

  const loadNotifications = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNotifications(false, 0, 20); // Get last 20 notifications
      setNotifications(data);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUnreadCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    
    // Listen for notification refresh events
    const cleanup = onNotificationRefresh(() => {
      loadUnreadCount();
      if (showDropdown) {
        loadNotifications();
      }
    });
    
    return () => {
      clearInterval(interval);
      cleanup();
    };
  }, [showDropdown, loadUnreadCount, loadNotifications]);

  useEffect(() => {
    if (showDropdown) {
      loadNotifications();
    }
  }, [showDropdown, loadNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleMarkAsRead(notificationId) {
    try {
      await markNotificationsAsRead([notificationId]);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  }

  async function handleMarkAllAsRead() {
    try {
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
      if (unreadIds.length === 0) return;
      
      await markNotificationsAsRead(unreadIds);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  }

  async function handleDelete(notificationId, event) {
    event.stopPropagation();
    try {
      await deleteNotification(notificationId);
      const wasUnread = notifications.find(n => n.id === notificationId)?.is_read === false;
      setNotifications(notifications.filter(n => n.id !== notificationId));
      if (wasUnread) {
        setUnreadCount(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  }

  function handleNotificationClick(notification) {
    // Mark as read if unread
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    const { notification_type } = notification;
    
    switch (notification_type) {
      case "partner_request":
      case "partner_accepted":
      case "partner_rejected":
        navigate("/find-partner");
        break;
      case "agency_query":
      case "agency_response":
      case "agency_accepted":
      case "agency_rejected":
      case "agency_request":
        navigate("/agencies");
        break;
      case "rating_received":
        navigate("/profile");
        break;
      case "new_destination_match":
        navigate("/find-partner");
        break;
      default:
        break;
    }
    
    setShowDropdown(false);
  }

  function getNotificationIcon(type) {
    const icons = {
      partner_request: "👥",
      partner_accepted: "✅",
      partner_rejected: "❌",
      agency_query: "💼",
      agency_response: "📨",
      agency_accepted: "✅",
      agency_rejected: "❌",
      agency_request: "🏢",
      new_destination_match: "✈️",
      rating_received: "⭐"
    };
    return icons[type] || "🔔";
  }

  function formatTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  }

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button 
        className="notification-bell-button"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {notifications.some(n => !n.is_read) && (
              <button 
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">
                <div className="loading-spinner-small"></div>
                <p>Loading...</p>
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? "unread" : ""}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">
                      {formatTimeAgo(notification.created_at)}
                    </div>
                  </div>
                  <button
                    className="notification-delete"
                    onClick={(e) => handleDelete(notification.id, e)}
                    title="Delete notification"
                  >
                    ×
                  </button>
                </div>
              ))
            ) : (
              <div className="notification-empty">
                <div className="empty-icon">🔔</div>
                <p>No notifications yet</p>
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <button 
                className="view-all-btn"
                onClick={() => {
                  navigate("/notifications");
                  setShowDropdown(false);
                }}
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
